const ACCEPT_ALL_FILE_TYPES = '*.*';
const UNSAFE_FILE_EXTENSIONS = new Set(['exe', 'scr', 'vbs', 'js', 'xml', 'docm', 'xps', 'iso', 'img', 'py']);

/**
 * Returns the accept value suitable for an HTML file input.
 *
 * The database `*.*` wildcard means any file with a usable extension. HTML file inputs
 * do not interpret that wildcard consistently, so it is represented by an empty value.
 */
export function toNativeFileInputAccept(accept: string | null): string {
  const normalizedAcceptTypes = getNormalizedAcceptTypes(accept);
  return normalizedAcceptTypes.length === 0 || normalizedAcceptTypes.includes(ACCEPT_ALL_FILE_TYPES)
    ? ''
    : (accept ?? '');
}

/**
 * Determines whether a browser file has a usable extension accepted by the configured formats.
 */
export function isFileAccepted(file: Pick<File, 'name' | 'type'>, accept: string | null): boolean {
  const extension = getFileExtension(file.name);
  if (extension == null) return false;
  if (isUnsafeFile(file)) return false;

  const normalizedAcceptTypes = getNormalizedAcceptTypes(accept);
  if (normalizedAcceptTypes.length === 0 || normalizedAcceptTypes.includes(ACCEPT_ALL_FILE_TYPES)) return true;

  const mimeType = file.type.toLowerCase();
  return normalizedAcceptTypes.some((acceptType: string): boolean => {
    if (acceptType.endsWith('/*')) {
      return mimeType.startsWith(acceptType.slice(0, -1));
    }

    if (acceptType.includes('/')) {
      return mimeType === acceptType;
    }

    return extension === acceptType.replace(/^\./, '');
  });
}

export function isUnsafeFile(file: Pick<File, 'name'>): boolean {
  const extension = getFileExtension(file.name);
  return extension != null && UNSAFE_FILE_EXTENSIONS.has(extension);
}

function getFileExtension(fileName: string): string | null {
  const extensionSeparatorIndex = fileName.lastIndexOf('.');
  if (extensionSeparatorIndex <= 0 || extensionSeparatorIndex === fileName.length - 1) return null;

  const extension = fileName
    .slice(extensionSeparatorIndex + 1)
    .trim()
    .toLowerCase();
  return extension.length > 0 ? extension : null;
}

function getNormalizedAcceptTypes(accept: string | null): string[] {
  return (accept ?? '')
    .split(',')
    .map((acceptType: string): string => acceptType.trim().toLowerCase())
    .filter((acceptType: string): boolean => acceptType.length > 0);
}
